<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"

import SlideoverFooter from "@/components/SlideoverFooter.vue"
import { peoplePath, personPath } from "@/routes"

const props = defineProps<{
  open: boolean
  person: { id: string; name: string; relationship: string | null; notes: string | null } | null
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const name = ref("")
const relationship = ref("")
const notes = ref("")
const saving = ref(false)
const formErrors = ref<Record<string, string>>({})

const isEdit = computed(() => !!props.person)
const title = computed(() => (isEdit.value ? "Edit Person" : "New Person"))
const submitLabel = computed(() => (isEdit.value ? "Save" : "Create Person"))

watch(
  () => props.open,
  (open) => {
    if (open) {
      name.value = props.person?.name ?? ""
      relationship.value = props.person?.relationship ?? ""
      notes.value = props.person?.notes ?? ""
      formErrors.value = {}
    }
  },
)

function close() {
  emit("update:open", false)
}

function submit() {
  saving.value = true
  const data = {
    person: {
      name: name.value,
      relationship: relationship.value || null,
      notes: notes.value || null,
    },
  }

  if (isEdit.value) {
    router.patch(personPath({ id: props.person!.id }), data, {
      onSuccess: () => close(),
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  } else {
    router.post(peoplePath(), data, {
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  }
}
</script>

<template>
  <USlideover :open="open" :title="title" @update:open="emit('update:open', $event)">
    <template #body>
      <form id="person-form" class="flex flex-col gap-6" @submit.prevent="submit">
        <UFormField label="Name" required :error="formErrors.name">
          <UInput
            v-model="name"
            placeholder="e.g. Jane Smith"
            maxlength="100"
            autofocus
            class="w-full"
          />
        </UFormField>

        <UFormField label="Relationship">
          <UInput
            v-model="relationship"
            placeholder="e.g. Neighbour, Coworker"
            maxlength="100"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Notes">
          <UTextarea v-model="notes" placeholder="Optional notes..." :rows="3" class="w-full" />
        </UFormField>
      </form>
    </template>

    <template #footer>
      <SlideoverFooter
        form-id="person-form"
        :submit-label="submitLabel"
        :loading="saving"
        @cancel="close"
      />
    </template>
  </USlideover>
</template>
